import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import PaywallModal from "@/components/PaywallModal";
import { ImportWaitNote, ImportSavedNote, ImportNextSteps, ImportDestination } from "@/components/ImportNotes";
import MyImports from "@/components/MyImports";
import { chunkedUpload, evernoteUpload, MAX_UPLOAD_BYTES } from "@/lib/chunkedUpload";
import { EnexPicker } from "@/components/EnexPicker";
import { SplitToggle, DupeSummary, DupeBadge, resolvePlan } from "@/components/ImportEvernoteExtras";
import { NotebookPlan } from "@/components/NotebookPlan";
import { ImportGuidePicker } from "@/components/ImportGuidePicker";
import { ImportErrorHelp } from "@/components/ImportErrorHelp";
import { ImportRetry } from "@/components/ImportRetry";
import {
  Upload, Globe, Camera, Bookmarks, CheckCircle, XCircle, Clock,
  ForkKnife, Sparkle, ArrowRight, CircleNotch,
} from "@phosphor-icons/react";

const TABS = [
  { key: "file", label: "Upload a file", icon: Upload,
    hint: "Paprika, Mela, Recipe Keeper, Cookmate, Copy Me That, Plan to Eat, Notion, Evernote, Google Keep, MealMaster, Apple Notes and more — up to 500MB, photos included." },
  { key: "url", label: "Paste URLs", icon: Globe,
    hint: "Any recipe URL from NYT Cooking, BBC Good Food, Bon Appétit, Serious Eats etc. One per line, up to 500." },
  { key: "bookmarks", label: "Browser bookmarks", icon: Bookmarks,
    hint: "Export your recipes folder from Chrome/Safari/Firefox as bookmarks.html — we'll scrape each one." },
  { key: "screenshot", label: "Screenshot", icon: Camera,
    hint: "For any apps like Clove, Osta, or Recime — screenshot your recipe and we'll OCR it." },
];

export default function Import() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { flags, limits } = useSiteFlags();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "file");
  const [jobId, setJobId] = useState(null);
  const [preview, setPreview] = useState(null);   // { source, status, recipes, progress_* }
  const [pollTick, setPollTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  const [cookbookTitle, setCookbookTitle] = useState("");
  const [destCookbookId, setDestCookbookId] = useState("");
  const [myCookbooks, setMyCookbooks] = useState([]);
  useEffect(() => {
    if (!user) return;
    api.get("/cookbooks").then((r) => setMyCookbooks(r.data.owned || [])).catch(() => {});
  }, [user]);
  const [paywall, setPaywall] = useState(null);    // { message, cap, current, selected }
  const [dupes, setDupes] = useState({});
  const [dupesReady, setDupesReady] = useState(false);
  const [splitBooks, setSplitBooks] = useState(true);
  const [plan, setPlan] = useState({});
  const nbCount = preview?.notebooks?.length || 0;
  const canSplit = nbCount > 0 && !destCookbookId;
  const split = canSplit && (nbCount === 1 || splitBooks);
  const tickedBooks = (preview?.notebooks || []).filter((nb) => [...selectedIdx].some((i) => preview?.recipes?.[i]?._notebook === nb));
  const buildPlan = () => Object.fromEntries(tickedBooks.map((nb) => {
    const { title, match } = resolvePlan(nb, plan, myCookbooks);
    return [nb, match ? { cookbook_id: match.cookbook_id } : { title }];
  }));
  const untickDupes = () => setSelectedIdx((s) => new Set([...s].filter((i) => !dupes[String(i)])));
  useEffect(() => {
    setDupes({});
    setDupesReady(false);
    if (!user || !jobId || preview?.status !== "ready" || preview?.committed) return;
    api.get(`/import/${jobId}/duplicates`).then((r) => {
      const d = r.data.duplicates || {};
      setDupes(d);
      setSelectedIdx((sel) => new Set([...sel].filter((i) => !d[String(i)])));
    }).catch(() => {}).finally(() => setDupesReady(true));
  }, [user, jobId, preview?.status, preview?.committed]);
  const pollRef = useRef(null);
  const autoCommitRef = useRef(false);   // set on mount if ?auto_commit=1

  // If we got here from a "Sign in to save" hand-off (or from /try's
  // login-wall) the URL carries `?job=X` and optionally `?auto_commit=1`.
  // Hydrate the import job the moment we mount so nothing feels lost.
  useEffect(() => {
    const j = params.get("job");
    if (j && !jobId) {
      setJobId(j);
      autoCommitRef.current = params.get("auto_commit") === "1";
      // Preserve tab if present, but drop the resume params so a page
      // refresh doesn't re-auto-commit.
      const t = params.get("tab");
      const nextParams = t ? { tab: t } : {};
      setParams(nextParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only sync tab into URL AFTER any ?job resume has been consumed, so
    // we don't clobber the resume params on initial mount.
    if (params.get("job")) return;
    setParams({ tab }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Arriving from a landing page CTA (?tab=file): bring the dropzone into view and focus it.
  useEffect(() => {
    if (params.get("tab") !== "file" || params.get("job")) return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="import-dropzone"]');
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Poll preview until status !== "fetching" -------------------------
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/import/preview/${jobId}`);
        if (cancelled) return;
        setPreview(r.data);
        if (r.data.status === "fetching") {
          pollRef.current = setTimeout(tick, 2000);
        } else if (r.data.recipes?.length) {
          // Auto-select all by default so users click "Save" and go.
          setSelectedIdx(new Set(r.data.recipes.map((_, i) => i)));
        }
      } catch (e) {
        toast.error("Could not fetch import preview");
      }
    };
    tick();
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [jobId, pollTick]);

  // ---- Auto-commit trigger (post-signup / post-login hand-off) ----------
  // Fires once when: (1) URL had ?auto_commit=1, (2) user is signed in,
  // (3) preview finished loading with at least one recipe. Handles the
  // /try flow and the general "Sign in to save" hand-off from /import.
  useEffect(() => {
    if (!autoCommitRef.current) return;
    if (!user) return;
    if (!preview || preview.status !== "ready" || preview.committed) return;
    if (!(preview.recipes || []).length || !dupesReady) return;
    autoCommitRef.current = false;        // one-shot
    toast.info("Welcome back — saving your import…");
    commit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, preview, dupesReady]);

  // ---- Tab handlers -----------------------------------------------------
  const [uploadPct, setUploadPct] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [uploadNote, setUploadNote] = useState("");
  const [enexFiles, setEnexFiles] = useState(null);
  const tooBig = (f) => f.size > MAX_UPLOAD_BYTES;
  const runUpload = async (fn) => {
    setBusy(true);
    setPreview(null);
    setJobId(null);
    setSelectedIdx(new Set());
    setUploadPct(0);
    try {
      const r = await fn();
      setJobId(r.job_id);
      toast.success(r.status === "fetching" ? "Uploaded — reading your file now" : `Detected ${r.label || r.source}${r.count ? ` — ${r.count} recipe(s)` : ""}`);
    } catch (e) {
      const msg = e.response ? (e.response.data?.detail_obj || e.response.data?.detail) : "The connection dropped. Pick the same file again and the upload will carry on where it stopped.";
      setUploadError(typeof msg === "string" || msg?.message ? msg : "Import failed");
    } finally {
      setBusy(false);
      setUploadPct(null);
      setUploadNote("");
    }
  };
  const pickFiles = (list) => {
    const files = [...(list || [])];
    if (!files.length) return;
    setUploadError("");
    const big = files.find(tooBig);
    if (big) {
      setPreview(null);
      setJobId(null);
      setUploadError(`"${big.name}" is ${(big.size / 1048576).toFixed(0)}MB — the largest file we can take is 500MB. Try exporting your Evernote notebooks one at a time.`);
      return;
    }
    if (files.every((f) => /\.enex$/i.test(f.name))) { setPreview(null); setJobId(null); setEnexFiles(files); return; }
    if (files.length > 1) { setUploadError("You can drop several files at once for Evernote (.enex) notebooks only — please pick one file."); return; }
    runUpload(() => chunkedUpload(files[0], setUploadPct, setUploadNote));
  };
  const startEnex = (files, filter) => {
    setEnexFiles(null);
    runUpload(() => evernoteUpload(files, filter, setUploadPct, setUploadNote));
  };

  const submitUrls = async (urlBlob) => {
    if (!urlBlob.trim()) { toast.error("Paste at least one URL"); return; }
    setBusy(true);
    setPreview(null);
    setJobId(null);
    try {
      const r = await api.post("/import/urls", { urls: [urlBlob] });
      setJobId(r.data.job_id);
      toast.success(`Queued ${r.data.url_count} URL(s) — fetching in the background`);
      setTimeout(() => document.querySelector('[data-testid="import-preview"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "URL import failed");
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!user) {
      toast.info("Sign in and we'll save these for you automatically.");
      const returnTo = `/import?job=${jobId}&auto_commit=1`;
      nav(`/login?next=${encodeURIComponent(returnTo)}`);
      return;
    }
    if (selectedIdx.size === 0) { toast.error("Select at least one recipe"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/import/${jobId}/commit`, {
        selected: [...selectedIdx],
        cookbook_id: destCookbookId || undefined,
        cookbook_title: destCookbookId || split ? undefined : (cookbookTitle.trim() || undefined),
        split_by_notebook: split,
        notebook_plan: split ? buildPlan() : undefined,
      });
      toast.success(`Imported ${r.data.inserted} recipe(s)` +
        (r.data.cookbooks?.length > 1 ? ` into ${r.data.cookbooks.length} cookbooks` : "") +
        (r.data.skipped_duplicates ? ` (${r.data.skipped_duplicates} skipped as duplicates)` : ""));
      nav(r.data.cookbooks?.length > 1 ? "/dashboard" : `/cb/${r.data.cookbook_id}?just_imported=1`);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      // Backend returns a structured 402 for free-tier batch-over-cap so we
      // can show a clear PaywallModal with the exact remaining slots.
      if (status === 402 && detail && typeof detail === "object" && detail.code === "free_tier_batch_over_cap") {
        setPaywall({
          message: detail.message,
          cap: detail.cap,
          current: detail.current_recipe_count,
          selected: detail.selected_count,
          remaining: detail.remaining_free_slots,
          subs_on: detail.subscriptions_enabled,
        });
      } else if (status === 402) {
        // Legacy 402 (bare message) — open the generic modal.
        setPaywall({ message: typeof detail === "string" ? detail : "Free tier limit reached." });
      } else {
        toast.error(typeof detail === "string" ? detail : "Commit failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleAll = () => {
    if (!preview?.recipes?.length) return;
    if (selectedIdx.size === preview.recipes.length) setSelectedIdx(new Set());
    else setSelectedIdx(new Set(preview.recipes.map((_, i) => i)));
  };

  const toggleOne = (i) => {
    const next = new Set(selectedIdx);
    if (next.has(i)) next.delete(i); else next.add(i);
    setSelectedIdx(next);
  };

  const activeTab = TABS.find((t) => t.key === tab) || TABS[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-10">
      <PageMeta
        title="Import your recipes — Paprika, Mela, Notion, Evernote & more"
        description="Bring your recipes to RecipeFiler in one click. We support Paprika, Mela, Recipe Keeper, Cookmate, Copy Me That, BigOven, Notion, Evernote, MealMaster, Apple Notes and any recipe URL. Free import, no lock-in."
        canonical="https://recipefiler.com/import"
      />
      <BreadcrumbJsonLd items={[
        { name: "Home", url: "https://recipefiler.com/" },
        { name: "Import", url: "https://recipefiler.com/import" },
      ]} />

      {/* Hero */}
      <header className="text-left mb-10">
        <div className="inline-flex items-center gap-2 rounded-full bg-rf-amber/10 text-rf-orange px-3 py-1 text-xs font-semibold uppercase tracking-wider">
          <Sparkle weight="fill" size={14} /> Bring your data
        </div>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight" data-testid="import-h1">
          Move every recipe you've saved anywhere into one shelf.
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-3xl">
          Drop an export from Paprika, Mela, Recipe Keeper, Cookmate, Copy Me That, BigOven,
          Notion, Evernote or Apple Notes — or paste a list of URLs — and we'll rebuild your
          cookbook here in under a minute. Photos, tags, and source links come with you.
        </p>
      </header>

      {/* Anon-friendly reassurance banner — first-timers landing from Google
          need to know the whole flow is free until "Save". */}
      {!user && <div className="mb-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50/60 p-4 flex items-start gap-3" data-testid="import-anon-banner">
        <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-emerald-900">Free to try. Preview every recipe. Sign up only when you're ready to save.</p>
          <p className="text-emerald-800/80 mt-0.5">
            Drop your file or paste your URLs — we'll parse everything and show you the whole cookbook.
            Nothing gets saved until you have an account. <strong>The free tier saves up to 10 recipes;</strong>{" "}
            if your batch is larger than that, you'll be asked to pick a subscription that fits when you tap Save
            (see <Link to="/pricing" className="underline font-semibold">pricing</Link>).
            {" "}<Link to="/try" className="underline font-semibold">Just want to try one? →</Link>
          </p>
        </div>
      </div>}

      {/* Tab bar */}
      {user && !jobId && <MyImports />}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-200 pb-4">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`import-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={"flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all " +
                (active ? "bg-rf-orange text-white shadow-sm" : "bg-slate-100 text-slate-700 hover:bg-slate-200")}
            >
              <Icon size={18} weight={active ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
      </div>
      <p className="text-sm text-slate-500 mb-6" data-testid="import-tab-hint">{activeTab.hint} <ImportGuidePicker /></p>

      {/* Tab content */}
      {tab === "file" && <FileTab onUpload={pickFiles} busy={busy} pct={uploadPct} error={uploadError} note={uploadNote} multiple />}
      {tab === "file" && enexFiles && <EnexPicker key={enexFiles.map((f) => f.name).join("|")} files={enexFiles} onStart={startEnex} onCancel={() => setEnexFiles(null)} />}
      {tab === "url" && <UrlTab onSubmit={submitUrls} busy={busy} />}
      {tab === "bookmarks" && <FileTab onUpload={pickFiles} busy={busy} pct={uploadPct} error={uploadError} note={uploadNote}
        accept=".html" hint="Chrome → Bookmark manager → ⋯ menu → Export bookmarks." />}
      {tab === "screenshot" && <ScreenshotTab />}
      {!preview && tab !== "screenshot" && <ImportNextSteps user={user} />}

      {/* Preview */}
      {preview && (
        <section className="mt-10" data-testid="import-preview">
          <PreviewPanel
            preview={preview}
            selectedIdx={selectedIdx}
            onToggle={toggleOne}
            onToggleAll={toggleAll}
            cookbookTitle={cookbookTitle}
            onCookbookTitleChange={setCookbookTitle}
            myCookbooks={myCookbooks}
            destCookbookId={destCookbookId}
            onDestChange={setDestCookbookId}
            onCommit={commit}
            busy={busy}
            user={user}
            freeLimit={limits?.free_recipe_limit}
            recipeCount={user?.recipe_count}
            isFreeTier={user?.subscription_tier === "free"}
            dupes={dupes}
            split={split}
            canSplit={canSplit && nbCount > 1}
            onSplitChange={setSplitBooks}
            plan={plan}
            setPlan={setPlan}
            onUntickDupes={untickDupes}
            onRetried={() => setPollTick((t) => t + 1)}
          />
        </section>
      )}

      {/* Cross-links to per-source landing pages (SEO + user reassurance) */}
      <FormatDirectory />

      {/* Paywall — batched imports over the free cap. */}
      <PaywallModal
        open={!!paywall}
        onClose={() => setPaywall(null)}
        message={paywall?.message}
        upgradesEnabled={paywall?.subs_on !== false}
        recipeCount={paywall?.current}
        freeLimit={paywall?.cap ?? limits?.free_recipe_limit}
      />
    </div>
  );
}


function FileTab({ onUpload, busy, accept, hint, pct, error, note, multiple }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const handle = (files) => { if (!busy) onUpload(files); };
  return (
    <div
      data-testid="import-dropzone"
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handle(e.dataTransfer.files); }}
      className={"border-2 border-dashed rounded-2xl p-10 text-center transition-colors cursor-pointer outline-none focus:ring-4 focus:ring-rf-amber/50 " +
        (dragging ? "border-rf-orange bg-rf-amber/10" : "border-slate-300 bg-slate-50 hover:bg-slate-100")}
      onClick={() => inputRef.current?.click()}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); inputRef.current?.click(); } }}
      tabIndex={0}
      role="button"
      aria-label="Upload a recipe export file"
    >
      <Upload size={40} className="mx-auto text-slate-400" />
      <p className="mt-3 font-semibold text-slate-700">Drop a file or click to pick one</p>
      <p className="mt-1 text-sm text-slate-500">{hint || "Files up to 500 MB. Evernote users can drop several notebook (.enex) files at once and choose what comes in. Detection is automatic."}</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept || ".paprikarecipes,.paprikarecipe,.melarecipes,.melarecipe,.mcb,.zip,.enex,.mmf,.mm,.csv,.json,.bxr,.txt,.html"}
        className="hidden"
        multiple={!!multiple}
        data-testid="import-file-input"
        onChange={(e) => { handle(e.target.files); e.target.value = ""; }}
      />
      {busy && pct !== null && pct < 100 ? (
        <div className="mt-4 max-w-sm mx-auto" data-testid="import-upload-progress">
          <div className="bg-slate-200 rounded-full h-2"><div className="bg-rf-orange h-2 rounded-full transition-[width]" style={{ width: `${pct}%` }} /></div>
          <p className="mt-2 text-rf-orange text-sm font-semibold">Uploading… {pct}%</p>
          {note && <p className="mt-1 text-sm font-semibold text-amber-700" data-testid="import-upload-note">{note}</p>}
        </div>
      ) : busy && (
        <p className="mt-4 text-rf-orange text-sm flex items-center justify-center gap-2">
          <CircleNotch className="animate-spin" size={16} /> Parsing…
        </p>
      )}
      {error && !busy && <ImportErrorHelp error={error} />}
    </div>
  );
}


function UrlTab({ onSubmit, busy }) {
  const [text, setText] = useState("");
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <label className="block text-sm font-semibold text-slate-700 mb-2">
        Paste up to 500 recipe URLs (one per line)
      </label>
      <textarea
        data-testid="import-url-textarea"
        className="w-full h-52 rounded-lg border border-slate-300 p-3 text-sm font-mono focus:ring-2 focus:ring-rf-amber focus:border-rf-orange"
        placeholder={"https://cooking.nytimes.com/recipes/1024118-lemon-chicken\nhttps://www.bbcgoodfood.com/recipes/beef-stew-dumplings"}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-slate-500">We'll fetch each one in parallel and rehost the hero photo so it never breaks.</p>
        <button
          data-testid="import-url-submit"
          onClick={() => onSubmit(text)}
          disabled={busy}
          className="px-5 py-2 rounded-full bg-rf-orange text-white text-sm font-semibold hover:bg-rf-orange/90 disabled:opacity-60"
        >
          {busy ? "Queuing…" : "Fetch recipes"}
        </button>
      </div>
    </div>
  );
}


function ScreenshotTab() {
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-6" data-testid="import-screenshot-tab">
      <div className="flex items-start gap-3">
        <Camera size={28} className="text-rf-orange mt-0.5" />
        <div>
          <h3 className="font-semibold text-slate-800">Bring recipes across from any other app.</h3>
          <p className="mt-2 text-sm text-slate-600">
            Whether you're leaving Clove, Osta or Recime, or your app just doesn't have an export button —
            open each recipe, take a screenshot, and drop it into our normal <Link to="/scan" className="text-rf-orange underline">Scan</Link> page.
            Our OCR + AI structuring pipeline turns any recipe screenshot into a fully editable RecipeFiler recipe,
            with hero photo intact.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {[
              { to: "/rescue-from-clove", label: "Clove users →" },
              { to: "/rescue-from-osta", label: "Osta users →" },
              { to: "/rescue-from-recime", label: "Recime users →" },
            ].map((l) => (
              <Link key={l.to} to={l.to}
                className="px-3 py-1.5 rounded-full bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 font-semibold">
                {l.label}
              </Link>
            ))}
          </div>
          <Link to="/scan" className="mt-5 inline-flex items-center gap-2 text-rf-orange font-semibold text-sm">
            Open the Scan page <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}


function PreviewPanel({ preview, selectedIdx, onToggle, onToggleAll, cookbookTitle,
                       onCookbookTitleChange, onCommit, busy, user, myCookbooks = [], destCookbookId, onDestChange,
                       freeLimit, recipeCount, isFreeTier, dupes = {}, split, canSplit, onSplitChange, plan, setPlan, onUntickDupes, onRetried }) {
  const recipes = preview.recipes || [];
  const fetching = preview.status === "fetching";
  const errored = preview.status === "error";
  const total = preview.progress_total || recipes.length;
  const done = preview.progress_done || recipes.length;
  const allSelected = recipes.length > 0 && selectedIdx.size === recipes.length;
  // Free-tier headroom hint — shown ONLY to authenticated free-tier users so
  // they can see exactly how many slots remain before they hit Save.
  const showCapHint = user && isFreeTier && Number.isInteger(freeLimit) && Number.isInteger(recipeCount);
  const remainingSlots = showCapHint ? Math.max(0, freeLimit - recipeCount) : null;
  const wouldExceed = showCapHint && selectedIdx.size > remainingSlots;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      {preview.committed && <ImportSavedNote cookbookId={preview.cookbook_id} cookbookTitle={preview.cookbook_title} />}
      <div className="border-b border-slate-200 p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1">
          <h2 className="text-xl font-display font-semibold" data-testid="import-preview-title">
            {fetching && preview.queued ? "Waiting in line — your import starts shortly…" : fetching ? (preview.progress_label === "MB read" ? "Reading your notes…" : preview.progress_label === "reading" ? "Reading your file and saving photos…" : "Fetching…") : errored ? "Something went wrong" : preview.committed ?
             `${recipes.length} recipe${recipes.length !== 1 ? "s" : ""} saved` :
             `${recipes.length} recipe${recipes.length !== 1 ? "s" : ""} ready to import`}
          </h2>
          {fetching && total > 0 && (
            <div className="mt-2 flex items-center gap-2 text-sm text-slate-600">
              <Clock size={14} className="text-rf-orange" />
              <span>{done} of {total} {preview.progress_label || "fetched"}</span>
              <div className="ml-2 flex-1 max-w-xs bg-slate-200 rounded-full h-1.5">
                <div className="bg-rf-orange h-1.5 rounded-full transition-all"
                     style={{ width: `${Math.min(100, (done / Math.max(1, total)) * 100)}%` }} />
              </div>
            </div>
          )}
          {fetching && <ImportWaitNote jobId={preview.job_id} user={user} cookbookName={preview.default_cookbook_title} />}
          {errored && <ImportErrorHelp testid="import-job-error" error={{ message: preview.error || "Please try again.", guide: preview.error_guide, guide_label: preview.error_guide_label }} />}
          {errored && (preview.retryable || preview.source === "evernote" || preview.source === "file") && <ImportRetry jobId={preview.job_id} retryable={preview.retryable} onRetried={onRetried} />}
        </div>
        {recipes.length > 0 && !fetching && !preview.committed && (
          <>
            <button
              onClick={onToggleAll}
              data-testid="import-toggle-all"
              className="text-sm font-semibold text-slate-700 hover:text-rf-orange">
              {allSelected ? "Deselect all" : "Select all"}
            </button>
            {user && myCookbooks.length > 0 && (
              <select
                value={destCookbookId}
                onChange={(e) => onDestChange(e.target.value)}
                data-testid="import-destination-select"
                className="text-sm rounded-lg border border-slate-300 px-3 py-1.5 bg-white max-w-[220px]"
              >
                <option value="">New cookbook</option>
                {myCookbooks.map((c) => <option key={c.cookbook_id} value={c.cookbook_id}>{c.title}</option>)}
              </select>
            )}
            {canSplit && <SplitToggle on={split} onChange={onSplitChange} />}
            {!destCookbookId && !split && <input
              type="text"
              placeholder="New cookbook name (optional)"
              value={cookbookTitle}
              onChange={(e) => onCookbookTitleChange(e.target.value)}
              data-testid="import-cookbook-title"
              className="text-sm rounded-lg border border-slate-300 px-3 py-1.5 focus:ring-2 focus:ring-rf-amber focus:border-rf-orange w-56"
            />}
            <button
              onClick={onCommit}
              disabled={busy || selectedIdx.size === 0}
              data-testid="import-commit-btn"
              className="px-5 py-2 rounded-full bg-rf-orange text-white text-sm font-semibold hover:bg-rf-orange/90 disabled:opacity-60">
              {user ? `Save ${selectedIdx.size}` : `Sign in to save (${selectedIdx.size})`}
            </button>
          </>
        )}
      </div>
      {recipes.length > 0 && !fetching && !preview.committed && (split ? (
        <NotebookPlan
          notebooks={(preview.notebooks || []).filter((nb) => [...selectedIdx].some((i) => recipes[i]?._notebook === nb))}
          counts={[...selectedIdx].reduce((c, i) => { const nb = recipes[i]?._notebook; if (nb) c[nb] = (c[nb] || 0) + 1; return c; }, {})}
          plan={plan} setPlan={setPlan} myCookbooks={myCookbooks} user={user} />
      ) : (
        <ImportDestination
          name={destCookbookId ? (myCookbooks.find((c) => c.cookbook_id === destCookbookId)?.title || "") : (cookbookTitle.trim() || preview.default_cookbook_title)}
          existing={!!destCookbookId} count={selectedIdx.size} user={user} />
      ))}
      {!fetching && !preview.committed && <DupeSummary count={Object.keys(dupes).length} ticked={[...selectedIdx].filter((i) => dupes[String(i)]).length} onUntick={onUntickDupes} />}
      {/* Rows */}
      <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
        {recipes.map((r, i) => (
          <label key={i}
                 data-testid={`import-row-${i}`}
                 className={"flex items-center gap-4 p-4 cursor-pointer transition-colors " +
                   (selectedIdx.has(i) ? "bg-amber-50/60" : "hover:bg-slate-50")}>
            <input
              type="checkbox"
              className={"w-5 h-5 accent-rf-orange" + (preview.committed ? " hidden" : "")}
              checked={selectedIdx.has(i)}
              disabled={!!preview.committed}
              onChange={() => onToggle(i)}
              data-testid={`import-row-check-${i}`}
            />
            <div className="w-14 h-14 rounded-lg bg-slate-100 shrink-0 overflow-hidden flex items-center justify-center">
              {r.image_url || r._image_path ? (
                <img src={r.image_url || `${process.env.REACT_APP_BACKEND_URL}/api/files/${r._image_path}`} alt="" className="w-full h-full object-cover" />
              ) : r.has_image ? (
                <ForkKnife size={24} className="text-rf-orange" />
              ) : (
                <ForkKnife size={20} className="text-slate-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-800 truncate">{r.title || <em className="text-slate-400">Untitled</em>}{!preview.committed && <DupeBadge books={dupes[i]} i={i} />}</p>
              <p className="text-xs text-slate-500 truncate">
                {(r.ingredients || []).length} ingredients · {(r.instructions || []).length} steps
                {r.creator_name && <> · {r.creator_name}</>}
                {r.source_url && <> · <a href={r.source_url} target="_blank" rel="noreferrer"
                  className="text-rf-orange underline">source</a></>}
              </p>
            </div>
          </label>
        ))}
        {!recipes.length && !fetching && !errored && (
          <p className="p-8 text-center text-slate-500 text-sm">No recipes yet — waiting for parse.</p>
        )}
      </div>
    </div>
  );
}


function FormatDirectory() {
  const [formats, setFormats] = useState([]);
  useEffect(() => {
    api.get("/import/formats").then((r) => setFormats(r.data.formats || [])).catch(() => {});
  }, []);
  const grouped = useMemo(() => {
    const by = { app: [], notes: [], legacy: [], web: [], rescue: [] };
    formats.forEach((f) => (by[f.kind] || by.app).push(f));
    return by;
  }, [formats]);
  const sections = [
    { key: "app", title: "Dedicated recipe apps", desc: "Direct file-import." },
    { key: "notes", title: "Notes & databases", desc: "Free-form recipe notes." },
    { key: "web", title: "Anywhere on the web", desc: "URL + browser bookmarks." },
    { key: "legacy", title: "Legacy formats", desc: "Old-school text archives." },
    { key: "rescue", title: "Screenshot rescue from any app", desc: "Snap the recipe + AI OCR rebuilds it." },
  ];
  // ItemList JSON-LD — helps Google understand this is a curated hub of
  // supported import sources. Each entry links to its own landing page so
  // the crawler follows through to per-source HowTo+FAQ schemas.
  const itemListLd = formats.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Every recipe app, notes app and website RecipeFiler can import from",
    "numberOfItems": formats.length,
    "itemListElement": formats.map((f, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": f.label,
      "url": `https://recipefiler.com/${f.slug}`,
    })),
  } : null;
  return (
    <section className="mt-16">
      {itemListLd && (
        <script type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListLd) }} />
      )}
      <h2 className="text-2xl font-display font-semibold mb-6">Every format we speak fluently</h2>
      <div className="grid gap-6 md:grid-cols-2">
        {sections.map((s) => (
          <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="font-semibold text-slate-800">{s.title}</h3>
            <p className="text-xs text-slate-500 mb-3">{s.desc}</p>
            <ul className="space-y-1 text-sm">
              {(grouped[s.key] || []).map((f) => (
                <li key={f.key}>
                  <Link to={`/${f.slug}`}
                    data-testid={`format-link-${f.key}`}
                    className="text-rf-orange hover:underline inline-flex items-center gap-1">
                    Import from {f.label} <ArrowRight size={14} />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}
